/**
 * Text-to-data AI — feature gate (`services.product_extraction` scope).
 */
import { useMemo } from 'react';
import { getAiConfig } from '../../hooks/useAiConfig';
import { useAiReachability } from '../../hooks/useAiReachability';
import {
    aiHasScope,
    AI_SCOPE_PRODUCT_EXTRACTION,
    useAiAssistentInfo,
} from '../../hooks/useAiAssistentInfo';

export interface TextToDataAiAvailability {
    featureEnabled: boolean;
    hasAiConfig: boolean;
    serverReachable: boolean;
    serverChecking: boolean;
    scopesLoading: boolean;
    scopesError: string | null;
    available: boolean;
}

export function useTextToDataAiAvailability(): TextToDataAiAvailability {
    const aiConfig = useMemo(() => getAiConfig(), []);
    const reach = useAiReachability(aiConfig);
    const me = useAiAssistentInfo();

    const scopes = me.status === 'ready' ? me.info.scopes : undefined;
    const scopesLoading = Boolean(aiConfig?.token) && me.status === 'loading';
    const scopesError = me.status === 'error' ? me.message : null;

    const featureEnabled = useMemo(
        () => aiHasScope(scopes, AI_SCOPE_PRODUCT_EXTRACTION),
        [scopes],
    );

    const hasAiConfig = Boolean(aiConfig?.token);
    const serverReachable = reach === 'ok';
    const serverChecking = reach === 'checking';
    const available =
        hasAiConfig
        && featureEnabled
        && serverReachable
        && !scopesLoading
        && !scopesError;

    return {
        featureEnabled: hasAiConfig ? featureEnabled : false,
        hasAiConfig,
        serverReachable,
        serverChecking,
        scopesLoading,
        scopesError,
        available,
    };
}
